<template>
    <div>
        <BForm @submit.prevent="handleLogin">
            <BFormGroup :label="t('E-mail')">
                <BFormInput type="email" v-model="loginData.username" autofocus required autocomplete="username"/>
            </BFormGroup>

            <BFormGroup :label="t('Password')" class="mt-3">
                <BFormInput type="password" v-model="loginData.password" required autocomplete="current-password" />
            </BFormGroup>

            <BFormInvalidFeedback :state="!authError">
              {{t('Please enter a correct username and password. Note that both fields may be case-sensitive.')}}
            </BFormInvalidFeedback>

            <BRow class="mt-3">
                <BCol class="mt-2">
                    <NuxtLink :to="localePath('/accounts/password_reset')">{{t('Forgot password?')}}</NuxtLink>
                </BCol>
                <BCol>
                  <div class="d-grid gap-2">
                    <BButton size="md" type="submit" block :disabled="inProgress">
                        <BSpinner small v-if="inProgress" />
                        {{t('Sign in')}}
                    </BButton>
                  </div>
                </BCol>
            </BRow>
        </BForm>
    </div>
</template>

<script setup lang="ts">
const {t,} = useI18n({ useScope: 'local' })
const localePath = useLocalePath()
const route = useRoute()

const props = defineProps({
  redirectSuccess: {
    type: String,
    default: null
  }
})

const { signIn } = useAuth()
const emit = defineEmits(['userLogin'])

// Get redirect URL from query parameter "next" or from props
// Validate that URL is relative (starts with /) to prevent open redirect vulnerability
const callbackUrl = computed(() => {
  const next = route.query.next
  const url = next ? String(next) : props.redirectSuccess
  
  if (!url) return null
  
  // Only allow relative URLs (starting with /) for security
  // Reject external URLs (http://, https://, //, etc.)
  if (url.startsWith('/') && !url.startsWith('//')) {
    return url
  }
  
  // If external URL provided, fallback to home page for security
  return null
})

const loginData = ref({
  username: '',
  password: ''
})
const authError = ref(false)
const inProgress = ref(false)

// Функция для извлечения сообщения об ошибке из разных форматов ответа
function extractErrorMessage(errorObj): string {
    // Проверяем разные пути, где может быть сообщение об ошибке
    if (errorObj?.response?.data?.detail > 0) {
        return errorObj.response.data.detail;
    }

    if (errorObj?.data?.non_field_errors?.length > 0) {
        return errorObj.data.non_field_errors[0];
    }
    
    if (errorObj?.non_field_errors?.length > 0) {
        return errorObj.non_field_errors[0];
    }
    
    if (errorObj?.response?.data?.non_field_errors?.length > 0) {
        return errorObj.response.data.non_field_errors[0];
    }
    
    if (errorObj?._data?.non_field_errors?.length > 0) {
        return errorObj._data.non_field_errors[0];
    }

    if (typeof errorObj === 'string') {
        return errorObj;
    }
    
    if (errorObj?.message) {
        return errorObj.message;
    }
    return 'Ошибка входа';
}

async function handleLogin() {
    inProgress.value = true
    authError.value = null

    try {
        const result = await signIn({
            username: loginData.value.username,
            password: loginData.value.password,
            redirect: false,
        }, {callbackUrl: callbackUrl.value})

        if (result?.error) {
            authError.value = extractErrorMessage(result.error);
        } else if (!result?.error) {
            // Clean up sessionStorage after successful login
            if (process.client) {
                sessionStorage.removeItem('auth_redirect_url')
            }
            
            // Redirect to original page or home
            // callbackUrl is already validated to be a relative path
            await navigateTo(callbackUrl.value || '/')
        }
    } catch (err) {
        authError.value = extractErrorMessage(err);
    } finally {
        inProgress.value = false
    }
}

</script>

<style lang="scss" scoped>
a {
  color: black;
}
</style>

<i18n lang="json">
{
  "ru": {
    "Please enter a correct username and password. Note that both fields may be case-sensitive.": "Пожалуйста, введите правильное имя пользователя и пароль. Обратите внимание, что оба поля могут быть чувствительны к регистру.",
    "Password": "Пароль",
    "E-mail": "E-mail",
    "Sign in": "Вход",
    "Forgot password?": "Забыли пароль?"
  }
}
</i18n>
